import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
} from "@mantine/core";
import { IconAlertCircle, IconPlus } from "@tabler/icons-react";
import {
  DeductionReleaseEligibleObjectType,
  DeductionReleaseMetadataType,
} from "../../../types/DeductionRelease";
import {
  createDeductionRelease,
  getDeductionReleaseDetail,
  getDeductionReleaseMetadata,
  getEligibleObjects,
  signDeductionRelease,
} from "../../../services/DeductionReleaseService";
import { pdf } from "@react-pdf/renderer";
import DeductionReleasePDF from "../../pdf/DeductionReleasePDF";

interface CreateDeductionReleaseModalProps {
  opened: boolean;
  onClose: () => void;
  contractId: number;
  addressSystemId: number;
  userId: number;
  getTranslation: (key: string) => string | null;
  onCreated: () => void;
}

export default function CreateDeductionReleaseModal({
  opened,
  onClose,
  contractId,
  addressSystemId,
  userId,
  getTranslation,
  onCreated,
}: Readonly<CreateDeductionReleaseModalProps>) {
  const t = useCallback(
    (key: string, fallback: string) => getTranslation(key) || fallback,
    [getTranslation],
  );

  const [objectType, setObjectType] = useState("0");
  const [description, setDescription] = useState("");
  const [valueChange, setValueChange] = useState<number | string>(0);
  const [foremanId, setForemanId] = useState<string | null>(null);
  const [contractorId, setContractorId] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<DeductionReleaseMetadataType | null>(
    null,
  );
  const [objects, setObjects] = useState<DeductionReleaseEligibleObjectType[]>(
    [],
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setObjectType("0");
    setDescription("");
    setValueChange(0);
    setForemanId(null);
    setContractorId(null);
    setSelectedIds([]);
    setError(null);
  }, []);

  useEffect(() => {
    if (!opened) return;
    resetForm();

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const meta = await getDeductionReleaseMetadata(
          contractId,
          addressSystemId,
          userId,
        );
        setMetadata(meta);
        const selfForeman = meta.foremen.find((f) => f.id === userId);
        if (selfForeman) setForemanId(String(selfForeman.id));
        const selfContractor = meta.contractorForemen.find(
          (f) => f.id === userId,
        );
        if (selfContractor) setContractorId(String(selfContractor.id));
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t("ERROR_LOAD_METADATA", "Failed to load form metadata"),
        );
      } finally {
        setLoading(false);
      }
    };

    void load();
    // Intentionally omit `t` / `resetForm`: translation updates must not wipe in-progress form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when modal context changes
  }, [opened, contractId, addressSystemId, userId]);

  useEffect(() => {
    if (!opened) return;

    const loadObjects = async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await getEligibleObjects(
          contractId,
          Number(objectType),
          addressSystemId,
          userId,
        );
        setObjects(rows);
        setSelectedIds([]);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t("ERROR_LOAD_OBJECTS", "Failed to load eligible objects"),
        );
        setObjects([]);
      } finally {
        setLoading(false);
      }
    };

    void loadObjects();
    // Intentionally omit `t`: translation updates must not reload/clear object selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when filter context changes
  }, [opened, objectType, contractId, addressSystemId, userId]);

  const totals = useMemo(() => {
    const selected = objects.filter((o) => selectedIds.includes(o.id));
    return {
      invoiced: selected.reduce((sum, o) => sum + o.totalPrice, 0),
      hold: selected.reduce((sum, o) => sum + o.holdPrice, 0),
    };
  }, [objects, selectedIds]);

  const allSelected =
    objects.length > 0 && selectedIds.length === objects.length;

  const handleToggleAll = (checked: boolean) => {
    setSelectedIds(checked ? objects.map((o) => o.id) : []);
  };

  const handleSave = async () => {
    setError(null);
    if (!description.trim()) {
      setError(t("ERROR_DESCRIPTION_REQUIRED", "Description is required"));
      return;
    }
    if (valueChange === "" || valueChange === null || valueChange === undefined) {
      setError(t("ERROR_VALUE_CHANGE_REQUIRED", "Depreciation is required"));
      return;
    }
    if (!foremanId) {
      setError(t("ERROR_FOREMAN_REQUIRED", "Foreman is required"));
      return;
    }
    if (!contractorId) {
      setError(
        t("ERROR_CONTRACTOR_REQUIRED", "Contractor supervisor is required"),
      );
      return;
    }
    if (selectedIds.length === 0) {
      setError(t("ERROR_OBJECTS_REQUIRED", "Select at least one object"));
      return;
    }

    setSaving(true);
    try {
      const created = await createDeductionRelease({
        contractId,
        addressSystemId,
        userId,
        objectType: Number(objectType),
        description: description.trim(),
        valueChange: Number(valueChange),
        primaryPersonId: Number(foremanId),
        secondaryPersonId: Number(contractorId),
        objectIds: selectedIds,
      });

      const detail = await getDeductionReleaseDetail(
        created.id,
        contractId,
        addressSystemId,
        userId,
      );
      const blob = await pdf(<DeductionReleasePDF detail={detail} />).toBlob();
      await signDeductionRelease(
        created.id,
        contractId,
        addressSystemId,
        userId,
        blob,
        `pidatysten_palautus_${contractId}_${created.id}.pdf`,
      );

      onCreated();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err instanceof Error ? err.message : null) ??
        t("ERROR_SAVE", "Failed to save deduction release");
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("CREATE_TITLE", "Create a new restoration of detentions")}
      size="xl"
    >
      <Stack gap="md">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {error}
          </Alert>
        )}

        {loading && !objects.length ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : (
          <>
            <Radio.Group
              label={t("FIELD_TYPE", "Type")}
              withAsterisk
              value={objectType}
              onChange={setObjectType}
            >
              <Group mt="xs">
                <Radio value="0" label={t("TYPE_MAINTENANCE", "Maintenance")} />
                <Radio
                  value="1"
                  label={t("TYPE_CONSTRUCTION", "Construction")}
                />
                <Radio value="2" label={t("TYPE_RAT", "RAT")} />
              </Group>
            </Radio.Group>

            <Textarea
              label={t(
                "FIELD_DESCRIPTION",
                "Further information / description",
              )}
              withAsterisk
              minRows={3}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
            />

            <NumberInput
              label={t("FIELD_DEPRECIATION", "Depreciation")}
              withAsterisk
              min={0}
              decimalScale={2}
              value={valueChange}
              onChange={setValueChange}
            />

            <Select
              label={t("FIELD_FOREMAN", "Foreman")}
              withAsterisk
              searchable
              data={(metadata?.foremen ?? []).map((u) => ({
                value: String(u.id),
                label: u.name,
              }))}
              value={foremanId}
              onChange={setForemanId}
              placeholder={t("PLACEHOLDER_CHOOSE", "Choose")}
            />

            <Select
              label={t("FIELD_CONTRACTOR_SUPERVISOR", "Contractor's supervisor")}
              withAsterisk
              searchable
              data={(metadata?.contractorForemen ?? []).map((u) => ({
                value: String(u.id),
                label: u.name,
              }))}
              value={contractorId}
              onChange={setContractorId}
              placeholder={t("PLACEHOLDER_CHOOSE", "Choose")}
            />

            <Text fw={600}>
              {t("ITEMS_WITH_ARRESTS", "Items with Arrests")}
            </Text>

            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={
                        selectedIds.length > 0 && !allSelected
                      }
                      onChange={(e) =>
                        handleToggleAll(e.currentTarget.checked)
                      }
                      aria-label={t("SELECT_ALL", "Select all")}
                    />
                  </Table.Th>
                  <Table.Th>{t("COL_DESTINATION", "Destination")}</Table.Th>
                  <Table.Th>{t("COL_STATUS", "Status")}</Table.Th>
                  <Table.Th>{t("COL_INVOICE_DATE", "Invoice date")}</Table.Th>
                  <Table.Th>{t("COL_INVOICED", "Invoiced €")}</Table.Th>
                  <Table.Th>{t("COL_ARRESTED", "Arrested")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {objects.map((obj) => (
                  <Table.Tr key={obj.id}>
                    <Table.Td>
                      <Checkbox
                        checked={selectedIds.includes(obj.id)}
                        onChange={(e) => {
                          const checked = e.currentTarget.checked;
                          setSelectedIds((prev) =>
                            checked
                              ? [...prev, obj.id]
                              : prev.filter((id) => id !== obj.id),
                          );
                        }}
                      />
                    </Table.Td>
                    <Table.Td>{obj.location ?? obj.id}</Table.Td>
                    <Table.Td>{obj.statusDescription ?? ""}</Table.Td>
                    <Table.Td>
                      {obj.invoiceDate
                        ? new Date(obj.invoiceDate).toLocaleDateString("fi-FI")
                        : ""}
                    </Table.Td>
                    <Table.Td>{obj.totalPrice.toFixed(2)}</Table.Td>
                    <Table.Td>{obj.holdPrice.toFixed(2)}</Table.Td>
                  </Table.Tr>
                ))}
                {objects.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center">
                        {t("NO_ELIGIBLE_OBJECTS", "No eligible objects")}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
              <Table.Tfoot>
                <Table.Tr>
                  <Table.Td />
                  <Table.Td colSpan={3}>
                    <Text fw={700}>{t("TOTAL", "TOTAL")}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={700}>{totals.invoiced.toFixed(2)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={700}>{totals.hold.toFixed(2)}</Text>
                  </Table.Td>
                </Table.Tr>
              </Table.Tfoot>
            </Table>

            <Group justify="flex-end">
              <Button variant="default" onClick={onClose} disabled={saving}>
                {t("BUTTON_CANCEL", "Cancel")}
              </Button>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => void handleSave()}
                loading={saving}
                disabled={objects.length === 0}
              >
                {t("BUTTON_SAVE", "Save")}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
